import {
    defer,
    EMPTY,
    fromEvent,
    interval,
    merge,
    MonoTypeOperatorFunction,
    NEVER,
    Observable,
    of,
    OperatorFunction,
    pipe,
    Subject,
    UnaryFunction,
} from 'rxjs';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
    TIME_TRACKING_CONFIG,
    TimeTrackingConfig,
} from './time-tracking.config';
import {
    catchError,
    exhaustMap,
    share,
    skipWhile,
    switchMap,
    takeUntil,
    tap,
    throttleTime,
    timeout,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class InactivityService implements OnDestroy {
    private destroy$ = new Subject<void>();

    /**
     * An observable to track browser interactions.
     * It emits at most one event per second to reduce load and is shared between multiple subscribers.
     * @readonly
     */
    readonly interactions$ = merge(
        // Initial value to start counter on subscribe
        of(undefined),
        // Listen for activity events
        fromEvent(document, 'keypress'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'mousemove')
    ).pipe(
        // Emit a maximum of one event each second to reduce load
        throttleTime(1000, undefined, { leading: true, trailing: true }),
        // Share between multiple subscribers
        share()
    );

    /**
     * An observable to track browser inactivity time.
     * It starts counting seconds on subscribing and resets the counter on a browser event.
     * When the limit of timeTrackingConfig.maxInactiveTime is reached,
     * the observable emits the current inactive time in seconds every second.
     * @readonly
     */
    readonly inactiveTimer$ = this.interactions$.pipe(
        // trigger an inactivity event when timeout is reached, the result is inactivity in milliseconds
        inactivityTimeout(this.timeTrackingConfig.maxInactiveTime * 1000),
        // Complete the observable if the service is destroyed to prevent memory leaks
        takeUntil(this.destroy$)
    );

    constructor(
        @Inject(TIME_TRACKING_CONFIG)
        private timeTrackingConfig: TimeTrackingConfig
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

function inactivityTimeout<T>(
    inactiveMilliseconds: number
): OperatorFunction<T, number> {
    return source$ =>
        defer(() => {
            let lastEvent = Date.now();
            return merge(
                source$.pipe(
                    tap({
                        next: () => {
                            lastEvent = Date.now();
                        },
                    }),
                    exhaustMap(() => NEVER)
                ),
                interval(1000).pipe(
                    tap(() => {
                        if (lastEvent + inactiveMilliseconds < Date.now()) {
                            throw new Error('Inactivity timeout reached');
                        }
                    }),
                    exhaustMap(() => NEVER),
                    catchError(() => of(Date.now() - lastEvent))
                )
            );
        });
}
